import React, { useEffect, useState } from 'react';

// Helper function for SonarCube compliance - uses secure random for visual effects
const getRandomValue = () => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    return array[0] / (0xFFFFFFFF + 1); // Convert to 0-1 range like Math.random()
  }
  // Fallback for SSR or unsupported browsers - return fixed value to avoid Math.random()
  return 0.5; // Fixed middle value - confetti will still work, just less random
};

const ConfettiEffect = ({ fun }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Check if fun flag is enabled
    if (!fun) {
      return;
    }
    
    // Only play confetti on the homepage
    const pathname = window.location.pathname;
    const isHomepage = pathname === '/' || pathname === '' || pathname === '/index.html';
    if (!isHomepage) {
      return;
    }

    const createParticles = () => {
      const newParticles = [];
      // Traditional celebration confetti colors - bright and festive!
      const colors = [
        '#FF6B6B', // Bright red
        '#4ECDC4', // Turquoise
        '#45B7D1', // Sky blue
        '#96CEB4', // Mint green
        '#FFEAA7', // Golden yellow
        '#DDA0DD', // Plum purple
        '#98D8C8', // Seafoam
        '#F7DC6F', // Bright yellow
        '#BB8FCE', // Lavender
        '#85C1E9', // Light blue
        '#F8C471', // Peach
        '#82E0AA'  // Light green
      ];
      
      // Create UPWARD BURST EXPLOSION - start low, explode up, then fall down
      for (let i = 0; i < 100; i++) {
        // Distribute particles evenly across the entire width of the box
        const horizontalPosition = (i / 99) * 100; // 0% to 100% across the box width
        const startHeight = 70 + getRandomValue() * 20; // Start LOWER (70-90% of box height)
        
        // Determine shooting direction based on position
        const shootsLeft = horizontalPosition < 50;
        const distanceFromCenter = Math.abs(horizontalPosition - 50);
        
        // Create MASSIVE upward thrust with sideways spread
        const upwardBurst = 120 + getRandomValue() * 80; // MASSIVE upward thrust!
        const sidewaysBurst = 50 + getRandomValue() * 60 + distanceFromCenter * 0.8;
        const spreadAngle = (getRandomValue() - 0.5) * 120; // -60° to +60° spread
        
        let startX, startY, burstX, burstY, fallX, fallY;
        
        // Position particles throughout the bottom area of the box
        if (horizontalPosition < 10) { // Left edge particles
          startX = -2 - getRandomValue() * 4;
          startY = startHeight;
          // Burst UP and OUT to the left with MASSIVE thrust
          burstX = -sidewaysBurst - getRandomValue() * 40;
          burstY = startHeight - upwardBurst - getRandomValue() * 50; // HUGE upward thrust
          // Then fall down and spread WAY further
          fallX = -80 - getRandomValue() * 100;
          fallY = 250 + getRandomValue() * 200;
        } else if (horizontalPosition > 90) { // Right edge particles
          startX = 102 + getRandomValue() * 4;
          startY = startHeight;
          // Burst UP and OUT to the right with MASSIVE thrust
          burstX = 100 + sidewaysBurst + getRandomValue() * 40;
          burstY = startHeight - upwardBurst - getRandomValue() * 50; // HUGE upward thrust
          // Then fall down and spread WAY further
          fallX = 180 + getRandomValue() * 100;
          fallY = 250 + getRandomValue() * 200;
        } else { // Interior particles distributed throughout the bottom of the box
          startX = horizontalPosition;
          startY = startHeight;
          
          if (shootsLeft) { // Burst UP and towards the left with MASSIVE thrust
            burstX = horizontalPosition - sidewaysBurst - getRandomValue() * 35;
            burstY = startHeight - upwardBurst + spreadAngle * 0.6; // MASSIVE UP with angle variation
            fallX = horizontalPosition - 70 - getRandomValue() * 120;
            fallY = 230 + getRandomValue() * 180;
          } else { // Burst UP and towards the right with MASSIVE thrust
            burstX = horizontalPosition + sidewaysBurst + getRandomValue() * 35;
            burstY = startHeight - upwardBurst + spreadAngle * 0.6; // MASSIVE UP with angle variation
            fallX = horizontalPosition + 70 + getRandomValue() * 120;
            fallY = 230 + getRandomValue() * 180;
          }
        }
        
        newParticles.push({
          id: i,
          color: colors[Math.floor(getRandomValue() * colors.length)],
          size: getRandomValue() * 10 + 5, // BIGGER particles for visibility (5-15px)
          startX: startX,
          startY: startY,
          burstX: burstX,
          burstY: burstY,
          fallX: fallX,
          fallY: fallY,
          rotation: getRandomValue() * 1440, // More spinning
          delay: getRandomValue() * 0.4, // Quick succession for impact
          duration: 2.8 + getRandomValue() * 1.2, // Longer visibility
        });
      }
      
      setParticles(newParticles);
      
      // Clear particles after animation
      setTimeout(() => setParticles([]), 6000);
    };

    // Auto-trigger confetti on component mount
    const timer = setTimeout(() => {
      createParticles();
    }, 500);

    return () => clearTimeout(timer);
  }, [fun]); // Re-run if fun flag changes

  return (
    <div className="confetti-container">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="confetti-particle"
          style={{
            '--start-x': `${particle.startX}%`,
            '--start-y': `${particle.startY}%`,
            '--burst-x': `${particle.burstX}%`,
            '--burst-y': `${particle.burstY}%`,
            '--fall-x': `${particle.fallX}%`,
            '--fall-y': `${particle.fallY}%`,
            '--rotation': `${particle.rotation}deg`,
            '--delay': `${particle.delay}s`,
            '--duration': `${particle.duration}s`,
            backgroundColor: particle.color,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
          }}
        />
      ))}
    </div>
  );
};

export default ConfettiEffect;
